import React, { useEffect, useState } from 'react';
import { Modal, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { PaymentTerminalView } from '../../../../services/credit-payments/credit-payments.service';
import { Button } from '../../../../components/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import type { FixedTerminalTarget } from '../../hooks/fixed-terminal.util';
import classes from './fixed-printer-modal.module.scss';

export type FixedTerminalModalProps = {
  opened: boolean;
  onClose: () => void;
  terminals: PaymentTerminalView[];
  fixedTerminalTarget: FixedTerminalTarget | null;
  onSelect: (target: FixedTerminalTarget) => void;
  onClear: () => void;
};

const FixedTerminalModalComponent: React.FC<FixedTerminalModalProps> = ({
  opened,
  onClose,
  terminals,
  fixedTerminalTarget,
  onSelect,
  onClear,
}) => {
  const [selectedTarget, setSelectedTarget] = useState<FixedTerminalTarget | null>(fixedTerminalTarget);
  const pinpads = terminals.flatMap((terminal) => terminal.pinpads.map((pinpad) => ({ terminal, pinpad })));

  useEffect(() => {
    if (opened) {
      setSelectedTarget(fixedTerminalTarget);
    }
  }, [opened, fixedTerminalTarget]);

  const handleSave = () => {
    if (selectedTarget == null) return;
    onSelect(selectedTarget);
    onClose();
  };

  const handleClear = () => {
    onClear();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onContextMenu={(event) => event.preventDefault()}
      title={<strong>קיבוע מסופון למכשיר</strong>}
      centered
      radius="md"
      dir="rtl"
      classNames={{ root: navyModal.root }}
      zIndex={400}
      closeButtonProps={{ 'data-testid': 'fixed-terminal-close' } as Record<string, string>}
    >
      <div className={classes.stack}>
        {pinpads.length === 0 ? (
          <span className={classes.empty}>אין נייח פעיל</span>
        ) : (
          <div className={classes.list}>
            {pinpads.map(({ terminal, pinpad }) => {
              const target = { terminalId: terminal.id, pinpadId: pinpad.pinpadId };
              const isSelected = selectedTarget?.terminalId === target.terminalId
                && selectedTarget.pinpadId === target.pinpadId;
              return (
                <UnstyledButton
                  key={`${terminal.id}:${pinpad.pinpadId}`}
                  component="button"
                  type="button"
                  data-testid={`fixed-terminal-option-${terminal.id}-${pinpad.pinpadId}`}
                  className={isSelected ? `${classes.item} ${classes.itemActive}` : classes.item}
                  onClick={() => setSelectedTarget(target)}
                >
                  <span>{pinpad.label ?? terminal.name}</span>
                  {isSelected && <FontAwesomeIcon icon={faCheck as IconProp} />}
                </UnstyledButton>
              );
            })}
          </div>
        )}
        <div className={classes.actions}>
          <Button variant="cancel" onClick={handleClear} disabled={fixedTerminalTarget == null} dataTestId="fixed-terminal-clear">
            נקה / בטל קיבוע
          </Button>
          <Button variant="save" onClick={handleSave} disabled={selectedTarget == null} dataTestId="fixed-terminal-save">
            שמור
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const FixedTerminalModal = React.memo(FixedTerminalModalComponent);